import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { SysManagementService, MaintenanceIdItem, Template, MaintenanceDetails, TemplateDetails } from '../../services/sys-management.service';
import { MaintenanceService, LoginNoticeItem } from '../../../services/maintenance.service';

@Component({
  selector: 'app-sys-maintenance',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sys-maintenance.html',
  styleUrl: './sys-maintenance.scss'
})
export class SysMaintenanceComponent implements OnInit {

  // 現在の告知表示のプロパティ
  currentNotices: LoginNoticeItem[] = [];
  currentNoticeLoading: boolean = false;
  currentNoticesCache: LoginNoticeItem[] | null = null;
  noticeDataChanged: boolean = false;

  // メンテナンス設定のプロパティ
  // メンテナンスID
  selectedMaintenanceId: string = '';
  maintenanceIds: MaintenanceIdItem[] = [];

  // 表示開始
  displayStartYear: number | null = null;
  displayStartMonth: number | null = null;
  displayStartDay: number | null = null;
  displayStartHour: number | null = null;
  displayStartMinute: number | null = null;
  setCurrentDate: boolean = false;

  // 表示終了
  displayEndYear: number | null = null;
  displayEndMonth: number | null = null;
  displayEndDay: number | null = null;
  displayEndHour: number | null = null;
  displayEndMinute: number | null = null;

  // テンプレートと告知内容
  selectedTemplate: string = '';
  noticeContent: string = '';

  // サービス停止開始
  stopStartYear: number | null = null;
  stopStartMonth: number | null = null;
  stopStartDay: number | null = null;
  stopStartHour: number | null = null;
  stopStartMinute: number | null = null;

  // サービス停止終了
  stopEndYear: number | null = null;
  stopEndMonth: number | null = null;
  stopEndDay: number | null = null;
  stopEndHour: number | null = null;
  stopEndMinute: number | null = null;

  // 代替URL
  alternativeUrl: string = '';

  // テンプレート設定
  selectedTemplateForEdit: string = '';
  templateName: string = '';
  templateType: string = '1'; // メールが既定
  templateTitle: string = '';
  templateContent: string = '';
  templateTarget: string = '0'; // 全員が既定
  senderEmail: string = '';

  // 選択肢データ
  templates: Template[] = [];
  editableTemplates: Template[] = []; // テンプレート編集用リスト

  constructor(
    private sysManagementService: SysManagementService,
    private maintenanceSvc: MaintenanceService
  ) { }

  ngOnInit(): void {
    // 初期データ読み込み（並列取得）
    forkJoin({
      status:    this.maintenanceSvc.getLoginMaintenanceStatus(),
      ids:       this.sysManagementService.getMaintenanceIds(),
      templates: this.sysManagementService.getEditableTemplates()
    }).subscribe({
      next: ({ status, ids, templates }) => {
        this.currentNotices      = status.notices;
        this.currentNoticesCache = status.notices;
        this.maintenanceIds      = ids;
        this.editableTemplates   = templates;
        this.templates = templates.filter(t => Number(t.templateType) === 2);
      },
      error: () => {
        this.currentNotices    = [];
        this.maintenanceIds    = [];
        this.editableTemplates = [];
        this.templates         = [];
      }
    });
  }

  // メンテナンス告知関連メソッド
  saveNotice(): void {
    // バリデーションチェック
    const validationError = this.validateMaintenanceData();
    if (validationError) {
      alert(validationError);
      return;
    }

    // データの正規化（欠けている値を補完）
    const normalizedData = this.normalizeMaintenanceData();

    // メンテナンスIDが選択されている場合は更新、そうでなければ挿入
    if (this.selectedMaintenanceId) {
      this.updateMaintenanceRecord(normalizedData);
    } else {
      this.insertMaintenanceRecord(normalizedData);
    }
  }

  clearNotice(): void {
    console.log('メンテナンス告知フォームをクリアします');
    this.selectedMaintenanceId = '';
    this.displayStartYear = null;
    this.displayStartMonth = null;
    this.displayStartDay = null;
    this.displayStartHour = null;
    this.displayStartMinute = null;
    this.setCurrentDate = false;
    this.displayEndYear = null;
    this.displayEndMonth = null;
    this.displayEndDay = null;
    this.displayEndHour = null;
    this.displayEndMinute = null;
    this.selectedTemplate = '';
    this.noticeContent = '';
    this.stopStartYear = null;
    this.stopStartMonth = null;
    this.stopStartDay = null;
    this.stopStartHour = null;
    this.stopStartMinute = null;
    this.stopEndYear = null;
    this.stopEndMonth = null;
    this.stopEndDay = null;
    this.stopEndHour = null;
    this.stopEndMinute = null;
    this.alternativeUrl = '';
  }

  deleteNotice(): void {
    // メンテナンスIDが選択されているかチェック
    if (!this.selectedMaintenanceId) {
      alert('削除するメンテナンスIDを選択してください');
      return;
    }

    // 確認ダイアログを表示
    const confirmed = confirm('選択されているメンテナンス情報を削除します。よろしいですか？');
    if (!confirmed) {
      return; // キャンセルされた場合は何もしない
    }

    // 削除処理を実行
    this.sysManagementService.deleteMaintenanceRecord(this.selectedMaintenanceId)
      .subscribe({
        next: (result: any) => {
          alert('メンテナンス情報を削除しました。');
          console.log('削除完了:', result);
          // フォームを初期状態に戻す
          this.clearNotice();
          // メンテナンスIDリストを更新
          this.loadMaintenanceIds();
          // キャッシュ無効化と最新データ取得
          this.invalidateNoticeCache();
          this.loadCurrentNotice();
        },
        error: () => {
          alert('メンテナンス情報の削除に失敗しました。');
        }
      });
  }

  // 現在の告知データを取得（キャッシュ機能付き）
  loadCurrentNotice(): void {
    // キャッシュがあり、データが変更されていない場合はキャッシュを使用
    if (this.currentNoticesCache !== null && !this.noticeDataChanged) {
      this.currentNotices = this.currentNoticesCache;
      return;
    }

    this.currentNoticeLoading = true;
    this.maintenanceSvc.getLoginMaintenanceStatus()
      .subscribe({
        next: (status) => {
          this.currentNotices      = status.notices;
          this.currentNoticesCache = status.notices;
          this.noticeDataChanged   = false;
          this.currentNoticeLoading = false;
        },
        error: () => {
          this.currentNotices      = [];
          this.currentNoticesCache = [];
          this.currentNoticeLoading = false;
        }
      });
  }

  // データ変更時にキャッシュを無効化
  invalidateNoticeCache(): void {
    this.noticeDataChanged = true;
    this.currentNoticesCache = null;
  }

  // メンテナンスIDリストを取得
  loadMaintenanceIds(): void {
    this.sysManagementService.getMaintenanceIds()
      .subscribe({
        next: (ids) => { this.maintenanceIds = ids; },
        error: ()   => { this.maintenanceIds = []; }
      });
  }

  // 現在日時をセットするチェックボックスの変更イベント
  onSetCurrentDateChange(): void {
    if (this.setCurrentDate) {
      const now = new Date();
      this.displayStartYear = now.getFullYear();
      this.displayStartMonth = now.getMonth() + 1;
      this.displayStartDay = now.getDate();
      this.displayStartHour = now.getHours();
      this.displayStartMinute = now.getMinutes();
    }
  }

  // メンテナンスID選択時のイベント
  onMaintenanceIdChange(): void {
    if (this.selectedMaintenanceId) {
      this.sysManagementService.getMaintenanceDetails(this.selectedMaintenanceId)
        .subscribe({
          next: (details: MaintenanceDetails | null) => {
            if (details) {
              this.setMaintenanceDetails(details);
            }
          },
          error: () => {
          }
        });
    }
  }

  // メンテナンス詳細をフォームにセット
  private setMaintenanceDetails(details: MaintenanceDetails): void {
    // 表示開始：NoticeStartDate
    if (details.noticeStartDate) {
      const startDate = new Date(details.noticeStartDate);
      this.displayStartYear = startDate.getFullYear();
      this.displayStartMonth = startDate.getMonth() + 1;
      this.displayStartDay = startDate.getDate();
      this.displayStartHour = startDate.getHours();
      this.displayStartMinute = startDate.getMinutes();
    }

    // 表示終了：NoticeEndDate
    if (details.noticeEndDate) {
      const endDate = new Date(details.noticeEndDate);
      this.displayEndYear = endDate.getFullYear();
      this.displayEndMonth = endDate.getMonth() + 1;
      this.displayEndDay = endDate.getDate();
      this.displayEndHour = endDate.getHours();
      this.displayEndMinute = endDate.getMinutes();
    }

    // 告知内容：NoticeText
    if (details.noticeText) {
      this.noticeContent = details.noticeText;
    }

    // サービス停止開始：SuspendStartDate
    if (details.suspendStartDate) {
      const suspendStart = new Date(details.suspendStartDate);
      this.stopStartYear = suspendStart.getFullYear();
      this.stopStartMonth = suspendStart.getMonth() + 1;
      this.stopStartDay = suspendStart.getDate();
      this.stopStartHour = suspendStart.getHours();
      this.stopStartMinute = suspendStart.getMinutes();
    }

    // サービス停止終了：SuspendEndDate
    if (details.suspendEndDate) {
      const suspendEnd = new Date(details.suspendEndDate);
      this.stopEndYear = suspendEnd.getFullYear();
      this.stopEndMonth = suspendEnd.getMonth() + 1;
      this.stopEndDay = suspendEnd.getDate();
      this.stopEndHour = suspendEnd.getHours();
      this.stopEndMinute = suspendEnd.getMinutes();
    }

    // 代替URL：AlternativeURL
    if (details.alternativeURL) {
      this.alternativeUrl = details.alternativeURL;
    }
  }

  // テンプレート選択時のイベント
  onTemplateChange(): void {
    if (this.selectedTemplate) {
      this.sysManagementService.getTemplateContent(this.selectedTemplate)
        .subscribe({
          next: (content: string | null) => {
            if (content) {
              this.noticeContent = content;
            }
          },
          error: () => {
          }
        });
    }
  }



  // バリデーションチェック
  private validateMaintenanceData(): string | null {
    const hasNoticeStart = this.hasValidDate(this.displayStartYear, this.displayStartMonth, this.displayStartDay);
    const hasSuspendStart = this.hasValidDate(this.stopStartYear, this.stopStartMonth, this.stopStartDay);

    // ①告知の表示期間、もしくはサービスの停止期間を指定してください
    if (!hasNoticeStart && !hasSuspendStart) {
      return '①告知の表示期間、もしくはサービスの停止期間を指定してください。';
    }

    // ②告知の終了日時を正しく指定してください
    if (hasNoticeStart) {
      if (!this.hasValidDate(this.displayEndYear, this.displayEndMonth, this.displayEndDay) ||
        !this.hasValidTime(this.displayEndHour)) {
        return '②告知の終了日時を正しく指定してください。';
      }
    }

    // ③サービスの終了日時を正しく指定してください
    if (hasSuspendStart) {
      if (!this.hasValidDate(this.stopEndYear, this.stopEndMonth, this.stopEndDay) ||
        !this.hasValidTime(this.stopEndHour)) {
        return '③サービスの終了日時を正しく指定してください。';
      }
    }

    // ④告知内容を入力してください
    if (hasNoticeStart && (!this.noticeContent || this.noticeContent.trim() === '')) {
      return '④告知内容を入力してください。';
    }

    // ⑤表示終了日時が表示開始日時より前である場合のチェック
    if (hasNoticeStart) {
      const noticeStart = this.createDateTime(
        this.displayStartYear!, this.displayStartMonth!, this.displayStartDay!,
        this.displayStartHour ?? 0, this.displayStartMinute ?? 0
      );
      const noticeEnd = this.createDateTime(
        this.displayEndYear!, this.displayEndMonth!, this.displayEndDay!,
        this.displayEndHour ?? 0, this.displayEndMinute ?? 0
      );
      if (noticeEnd <= noticeStart) {
        return '⑤表示終了日時は表示開始日時より後に設定してください。';
      }
    }

    // ⑥サービス停止終了日時がサービス停止開始日時より前である場合のチェック
    if (hasSuspendStart) {
      const suspendStart = this.createDateTime(
        this.stopStartYear!, this.stopStartMonth!, this.stopStartDay!,
        this.stopStartHour ?? 0, this.stopStartMinute ?? 0
      );
      const suspendEnd = this.createDateTime(
        this.stopEndYear!, this.stopEndMonth!, this.stopEndDay!,
        this.stopEndHour ?? 0, this.stopEndMinute ?? 0
      );
      if (suspendEnd <= suspendStart) {
        return '⑥サービス停止終了日時はサービス停止開始日時より後に設定してください。';
      }
    }

    return null; // バリデーション成功
  }

  // 日時オブジェクトを作成
  private createDateTime(year: number, month: number, day: number, hour: number, minute: number): Date {
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  }

  // 日付の有効性チェック
  private hasValidDate(year: number | null, month: number | null, day: number | null): boolean {
    return year !== null && month !== null && day !== null;
  }

  // 時刻の有効性チェック
  private hasValidTime(hour: number | null): boolean {
    return hour !== null;
  }

  // ローカル日時をタイムゾーンなし文字列（YYYY-MM-DDTHH:mm:ss）に変換
  // JSON.stringify が toISOString() を呼んでUTC変換するのを防ぐ
  private toLocalISOString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  }

  // データの正規化（欠けている値を補完）
  private normalizeMaintenanceData(): any {
    const data: any = {};

    // 告知期間の正規化
    if (this.hasValidDate(this.displayStartYear, this.displayStartMonth, this.displayStartDay)) {
      // 表示開始の時・分が未入力の場合は0をセット
      const startHour = this.displayStartHour ?? 0;
      const startMinute = this.displayStartMinute ?? 0;
      data.noticeStartDate = this.toLocalISOString(new Date(this.displayStartYear!, this.displayStartMonth! - 1, this.displayStartDay!, startHour, startMinute));

      // 表示終了の年・月が未入力の場合は表示開始と同じ値をセット、分が未入力の場合は0をセット
      const endYear = this.displayEndYear ?? this.displayStartYear;
      const endMonth = this.displayEndMonth ?? this.displayStartMonth;
      const endDay = this.displayEndDay!;
      const endHour = this.displayEndHour!;
      const endMinute = this.displayEndMinute ?? 0;
      data.noticeEndDate = this.toLocalISOString(new Date(endYear!, endMonth! - 1, endDay, endHour, endMinute));

      data.noticeText = this.noticeContent;
    }

    // サービス停止期間の正規化
    if (this.hasValidDate(this.stopStartYear, this.stopStartMonth, this.stopStartDay)) {
      // サービス停止開始の時・分が未入力の場合は0をセット
      const suspendStartHour = this.stopStartHour ?? 0;
      const suspendStartMinute = this.stopStartMinute ?? 0;
      data.suspendStartDate = this.toLocalISOString(new Date(this.stopStartYear!, this.stopStartMonth! - 1, this.stopStartDay!, suspendStartHour, suspendStartMinute));

      // サービス停止終了の年・月が未入力の場合はサービス停止開始と同じ値をセット、分が未入力の場合は0をセット
      const suspendEndYear = this.stopEndYear ?? this.stopStartYear;
      const suspendEndMonth = this.stopEndMonth ?? this.stopStartMonth;
      const suspendEndDay = this.stopEndDay!;
      const suspendEndHour = this.stopEndHour!;
      const suspendEndMinute = this.stopEndMinute ?? 0;
      data.suspendEndDate = this.toLocalISOString(new Date(suspendEndYear!, suspendEndMonth! - 1, suspendEndDay, suspendEndHour, suspendEndMinute));
    }

    // 代替URL
    if (this.alternativeUrl && this.alternativeUrl.trim() !== '') {
      data.alternativeURL = this.alternativeUrl;
    }

    // Validフラグを常に1にセット
    data.valid = true;

    return data;
  }

  // メンテナンス記録を更新
  private updateMaintenanceRecord(data: any): void {
    this.sysManagementService.updateMaintenanceRecord(this.selectedMaintenanceId, data)
      .subscribe({
        next: (result: any) => {
          alert('メンテナンス情報を更新しました。');
          console.log('更新完了:', result);
          this.invalidateNoticeCache(); // キャッシュ無効化
          this.loadCurrentNotice(); // 最新データを再取得
          this.clearNotice(); // フォームをクリア
          this.loadMaintenanceIds(); // メンテナンスIDリストを再読み込み
        },
        error: () => {
          alert('メンテナンス情報の更新に失敗しました。');
        }
      });
  }

  // メンテナンス記録を挿入
  private insertMaintenanceRecord(data: any): void {
    this.sysManagementService.insertMaintenanceRecord(data)
      .subscribe({
        next: (result: any) => {
          alert('メンテナンス情報を作成しました。');
          console.log('作成完了:', result);
          this.invalidateNoticeCache(); // キャッシュ無効化
          this.loadCurrentNotice(); // 最新データを再取得
          this.clearNotice(); // フォームをクリア
          this.loadMaintenanceIds(); // 新しいメンテナンスIDリストを再読み込み
        },
        error: () => {
          alert('メンテナンス情報の作成に失敗しました。');
        }
      });
  }

  // テンプレートリストを取得（editableTemplates から派生）
  loadEditableTemplates(): void {
    this.sysManagementService.getEditableTemplates()
      .subscribe({
        next: (templates: Template[]) => {
          this.editableTemplates = templates;
          this.templates = templates.filter(t => Number(t.templateType) === 2);
        },
        error: () => {
          this.editableTemplates = [];
          this.templates         = [];
        }
      });
  }

  // テンプレート編集用選択変更時
  onTemplateForEditChange(): void {
    if (this.selectedTemplateForEdit) {
      this.sysManagementService.getTemplateDetails(this.selectedTemplateForEdit)
        .subscribe({
          next: (details: TemplateDetails | null) => {
            if (details) {
              this.setTemplateDetails(details);
            }
          },
          error: () => {
          }
        });
    }
  }

  // テンプレート詳細をフォームにセット
  private setTemplateDetails(details: TemplateDetails): void {
    this.templateName = details.templateName || '';
    this.templateType = details.templateType?.toString() || '1';
    this.templateTitle = details.title || '';
    this.templateContent = details.content || '';
    this.templateTarget = details.target?.toString() || '0';
    this.senderEmail = details.defaultSender || '';
  }

  // テンプレート保存/更新
  saveTemplate(): void {
    // バリデーションチェック
    const validationError = this.validateTemplateData();
    if (validationError) {
      alert(validationError);
      return;
    }

    // 保存データ準備
    const templateData = {
      templateName: this.templateName,
      templateType: parseInt(this.templateType),
      title: this.templateTitle,
      content: this.templateContent,
      target: parseInt(this.templateTarget),
      defaultSender: this.senderEmail
    };

    // テンプレートIDが選択されている場合は更新、そうでなければ挿入
    if (this.selectedTemplateForEdit) {
      this.updateTemplate(templateData);
    } else {
      this.insertTemplate(templateData);
    }
  }

  // テンプレートデータのバリデーション
  private validateTemplateData(): string | null {
    // ①テンプレート名が入力されている
    if (!this.templateName || this.templateName.trim() === '') {
      return '①テンプレート名を入力してください。';
    }

    // ②テンプレート種類で「メール」が選択されている場合は「タイトル」も入力されている
    if (this.templateType === '1' && (!this.templateTitle || this.templateTitle.trim() === '')) {
      return '②メールのタイトルを入力してください。';
    }

    // ③内容が入力されている
    if (!this.templateContent || this.templateContent.trim() === '') {
      return '③内容を入力してください。';
    }

    return null; // バリデーション成功
  }

  // テンプレート更新
  private updateTemplate(data: any): void {
    this.sysManagementService.updateTemplate(this.selectedTemplateForEdit, data)
      .subscribe({
        next: (result: any) => {
          alert('テンプレート情報を更新しました。');
          console.log('テンプレート更新完了:', result);
          this.clearTemplate();
          this.loadEditableTemplates();
        },
        error: () => {
          alert('テンプレート情報の更新に失敗しました。');
        }
      });
  }

  // テンプレート挿入
  private insertTemplate(data: any): void {
    this.sysManagementService.insertTemplate(data)
      .subscribe({
        next: (result: any) => {
          alert('テンプレート情報を作成しました。');
          console.log('テンプレート作成完了:', result);
          this.clearTemplate();
          this.loadEditableTemplates();
        },
        error: () => {
          alert('テンプレート情報の作成に失敗しました。');
        }
      });
  }

  // テンプレートクリア
  clearTemplate(): void {
    this.selectedTemplateForEdit = '';
    this.templateName = '';
    this.templateType = '1';
    this.templateTitle = '';
    this.templateContent = '';
    this.templateTarget = '0';
    this.senderEmail = '';
  }

  // テンプレート削除
  deleteTemplate(): void {
    // テンプレートIDが選択されているかチェック
    if (!this.selectedTemplateForEdit) {
      alert('削除するテンプレート名を選択してください');
      return;
    }

    // 確認ダイアログを表示
    const confirmed = confirm('選択されているテンプレート情報を削除します。よろしいですか？');
    if (!confirmed) {
      return; // キャンセルされた場合は何もしない
    }

    // 削除処理を実行
    this.sysManagementService.deleteTemplate(this.selectedTemplateForEdit)
      .subscribe({
        next: (result: any) => {
          alert('テンプレート情報を削除しました。');
          console.log('テンプレート削除完了:', result);
          this.clearTemplate();
          this.loadEditableTemplates();
        },
        error: () => {
          alert('テンプレート情報の削除に失敗しました。');
        }
      });
  }

}
